/**
 * @description 小鹿足迹 Landing Page - Magic UI Style
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDaysIcon, BuildingOffice2Icon, MapPinIcon, BoltIcon } from '@heroicons/react/24/outline'
import LanguageSwitcher from '../../components/LanguageSwitcher.js'
import {
  trackPageView,
  trackAppStoreClick,
  trackScreenshotClick,
  trackScreenshotView,
  trackButtonClick,
} from '../../utils/analytics.js'

export default function HomePage() {
  const { t } = useTranslation()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isVisible, setIsVisible] = useState(false)

  const screenshots = useMemo(
    () => [
      {
        src: '/screenshots/2025-07-09/en-timeline1.PNG',
        alt: 'Timeline View',
        title: t('screenshots.timeline'),
      },
      {
        src: '/screenshots/2025-07-09/en-timeline2.PNG',
        alt: 'Timeline Detail',
        title: t('screenshots.timelineDetail'),
      },
      {
        src: '/screenshots/2025-07-09/en-report1.PNG',
        alt: 'Report View',
        title: t('screenshots.report'),
      },
      {
        src: '/screenshots/2025-07-09/en-report2.PNG',
        alt: 'Report Detail',
        title: t('screenshots.reportDetail'),
      },
      {
        src: '/screenshots/2025-07-09/en-me.PNG',
        alt: 'Profile View',
        title: t('screenshots.profile'),
      },
      {
        src: '/screenshots/2025-07-09/en-detail.PNG',
        alt: 'Activity Detail',
        title: t('screenshots.activityDetail'),
      },
      {
        src: '/screenshots/2025-07-09/en-travel.PNG',
        alt: 'Travel View',
        title: t('screenshots.travel'),
      },
    ],
    [t],
  )

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => {
      const newSlide = (prev + 1) % screenshots.length
      // Track automatic slide change
      if (screenshots[newSlide]) {
        trackScreenshotView(screenshots[newSlide].alt)
      }
      return newSlide
    })
  }, [screenshots])

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + screenshots.length) % screenshots.length)
  }

  const scrollToDemo = () => {
    trackButtonClick('demo_button', 'hero_section')
    const featuresSection = document.getElementById('features')
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const interval = setInterval(nextSlide, 4000)
    return () => clearInterval(interval)
  }, [nextSlide])

  useEffect(() => {
    setIsVisible(true)
    // Track page view
    trackPageView('/home', 'Retrace - Home')
  }, [])

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 text-slate-900 selection:bg-blue-500/30">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/40 via-transparent to-transparent" />
        <div className="absolute inset-0">
          {/* Floating orbs */}
          <div className="absolute left-1/4 top-1/4 h-48 w-48 animate-pulse rounded-full bg-blue-200/30 blur-3xl md:h-64 md:w-64" />
          <div
            className="absolute right-1/4 top-3/4 h-64 w-64 animate-pulse rounded-full bg-purple-200/30 blur-3xl md:h-96 md:w-96"
            style={{ animationDelay: '2s' }}
          />
          <div
            className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 transform animate-pulse rounded-full bg-indigo-200/20 blur-3xl md:h-[32rem] md:w-[32rem]"
            style={{ animationDelay: '4s' }}
          />
        </div>
        {/* Dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(#000 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
          }}
        />
      </div>

      {/* Navigation */}
      <nav className="fixed left-0 right-0 top-0 z-50 border-b border-white/50 bg-white/60 px-4 py-3 backdrop-blur-md transition-all duration-300 sm:px-6 sm:py-4 md:px-12">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex min-w-0 items-center space-x-2 sm:space-x-3">
            <div className="relative flex-shrink-0 overflow-hidden rounded-xl shadow-lg shadow-blue-500/20 transition-transform hover:scale-105">
              <img src="/imgs/icon.png" alt="Retrace" className="h-8 w-8 object-cover sm:h-10 sm:w-10" />
              <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent" />
            </div>
            <span className="bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-lg font-bold text-transparent sm:text-xl md:text-2xl">
              {t('nav.appName')}
            </span>
          </div>
          <div className="flex flex-shrink-0 items-center space-x-2 sm:space-x-4">
            <LanguageSwitcher />
            <button
              onClick={() => {
                trackAppStoreClick()
                window.open('https://apps.apple.com/app/6481981341', '_blank')
              }}
              className="group relative inline-flex items-center justify-center overflow-hidden rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-slate-900/20 transition-all duration-300 hover:scale-105 hover:bg-slate-800 hover:shadow-xl hover:shadow-slate-900/30 sm:px-6 sm:py-2.5 md:px-8 md:py-3 md:text-base"
            >
              <span className="relative flex items-center space-x-1.5 sm:space-x-2">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <span className="hidden sm:inline">{t('nav.appStore')}</span>
                <span className="sm:hidden">{t('nav.download')}</span>
              </span>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 px-4 pb-16 pt-28 sm:px-6 md:px-12 lg:pb-24 lg:pt-40">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
            <div
              className={`text-center transition-all duration-1000 lg:text-left ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
            >
              {/* Animated Badge */}
              <div className="mb-8 inline-flex items-center rounded-full border border-blue-100 bg-blue-50/80 px-4 py-1.5 shadow-sm backdrop-blur-sm transition-all duration-300 hover:border-blue-200 hover:bg-blue-100/80 hover:shadow-md">
                <span className="relative mr-2 flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
                </span>
                <span className="text-sm font-medium text-slate-600">{t('hero.badge')}</span>
              </div>

              {/* Main Title */}
              <h1 className="mb-6 font-serif text-3xl font-bold leading-tight text-slate-900 min-[400px]:text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
                <span className="block">{t('hero.title.line1')}</span>
                <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {t('hero.title.line2')}
                </span>
              </h1>

              {/* Subtitle */}
              <p className="mb-8 text-base leading-relaxed text-slate-500 sm:text-lg md:text-xl lg:text-2xl">
                {t('hero.subtitle')}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col justify-center gap-4 sm:flex-row lg:justify-start">
                <button
                  onClick={() => {
                    trackAppStoreClick()
                    window.open('https://apps.apple.com/app/6481981341', '_blank')
                  }}
                  className="group relative inline-flex transform items-center justify-center whitespace-nowrap rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3.5 text-base font-medium text-white shadow-lg shadow-blue-500/20 transition-all duration-300 hover:scale-105 hover:from-blue-700 hover:to-purple-700 hover:shadow-xl hover:shadow-blue-500/30 sm:px-8 sm:py-4 sm:text-lg"
                >
                  <svg
                    className="relative z-10 mr-2.5 h-5 w-5 transition-transform group-hover:-translate-y-0.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                  <span className="relative z-10">{t('hero.cta.download')}</span>
                </button>

                <button
                  onClick={scrollToDemo}
                  className="group inline-flex items-center justify-center whitespace-nowrap rounded-full border border-slate-200 bg-white px-6 py-3.5 text-base font-semibold text-slate-700 shadow-sm transition-all duration-300 hover:border-slate-300 hover:bg-slate-50 hover:shadow-md sm:px-8 sm:py-4 sm:text-lg"
                >
                  <svg
                    className="mr-3 h-5 w-5 text-slate-500 transition-transform duration-300 group-hover:translate-y-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                  <span>{t('hero.cta.explore', 'Explore Features')}</span>
                </button>
              </div>
            </div>

            {/* 3D Phone Carousel */}
            <div
              className={`screenshot-gallery relative transition-all delay-300 duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}
            >
              <div
                className="relative mx-auto h-[560px] w-80 md:h-[720px] md:w-[400px]"
                style={{ perspective: '1000px' }}
              >
                {screenshots.map((screenshot, index) => {
                  const isActive = index === currentSlide
                  const isNext = index === (currentSlide + 1) % screenshots.length
                  const isPrev = index === (currentSlide - 1 + screenshots.length) % screenshots.length

                  let transform = 'translateX(-50%) translateZ(-200px) rotateY(-30deg)'
                  let zIndex = 1
                  let opacity = 0.3
                  let scale = 0.8

                  if (isActive) {
                    transform = 'translateX(-50%) translateZ(0px) rotateY(0deg)'
                    zIndex = 10
                    opacity = 1
                    scale = 1
                  } else if (isNext) {
                    transform = 'translateX(-50%) translateX(120px) translateZ(-150px) rotateY(-25deg)'
                    zIndex = 5
                    opacity = 0.6
                    scale = 0.85
                  } else if (isPrev) {
                    transform = 'translateX(-50%) translateX(-120px) translateZ(-150px) rotateY(25deg)'
                    zIndex = 5
                    opacity = 0.6
                    scale = 0.85
                  }

                  return (
                    <div
                      key={index}
                      className="absolute left-1/2 top-0 h-[480px] w-56 cursor-pointer transition-all duration-700 ease-out md:h-[640px] md:w-80"
                      style={{
                        transform,
                        zIndex,
                        opacity,
                        transformStyle: 'preserve-3d',
                      }}
                      onClick={() => {
                        if (isActive) {
                          trackScreenshotClick(screenshot.alt)
                          window.open(screenshot.src, '_blank')
                        } else {
                          setCurrentSlide(index)
                          trackScreenshotView(screenshot.alt)
                        }
                      }}
                    >
                      <div className="relative h-full w-full">
                        <img
                          src={screenshot.src}
                          alt={screenshot.alt}
                          className="h-full w-full rounded-[2.5rem] border-[8px] border-white bg-slate-100 object-cover shadow-2xl shadow-slate-400/50"
                          style={{
                            transform: `scale(${scale})`,
                            transition: 'transform 0.7s ease-out',
                            filter: isActive ? 'none' : 'brightness(0.95) contrast(0.9)',
                          }}
                        />

                        {/* Glow Effect - Light Mode */}
                        {isActive && (
                          <div className="absolute inset-0 -z-10 scale-105 rounded-[3rem] bg-gradient-to-b from-blue-200/50 to-purple-200/50 blur-2xl" />
                        )}
                      </div>
                    </div>
                  )
                })}

                {/* Navigation buttons */}
                <button
                  onClick={() => {
                    trackButtonClick('screenshot_prev', 'carousel')
                    prevSlide()
                  }}
                  className="absolute -left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 transform items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-600 shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white sm:left-0 sm:h-12 sm:w-12"
                >
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <button
                  onClick={() => {
                    trackButtonClick('screenshot_next', 'carousel')
                    nextSlide()
                  }}
                  className="absolute -right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 transform items-center justify-center rounded-full border border-slate-200 bg-white/80 text-slate-600 shadow-lg backdrop-blur-sm transition-all duration-200 hover:scale-110 hover:bg-white sm:right-0 sm:h-12 sm:w-12"
                >
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              {/* Slide indicators */}
              <div className="mt-8 flex justify-center space-x-2">
                {screenshots.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      trackButtonClick(`screenshot_indicator_${index}`, 'carousel')
                      setCurrentSlide(index)
                    }}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentSlide ? 'w-8 bg-blue-500' : 'w-2 bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>

              {/* Screenshot title */}
              <div className="mt-4 text-center">
                <h3 className="font-serif text-xl font-semibold text-slate-900 transition-all duration-300">
                  {screenshots[currentSlide]?.title || ''}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {currentSlide + 1} / {screenshots.length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative z-10 bg-slate-50/50 px-4 py-16 sm:px-6 sm:py-24 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 text-center sm:mb-16">
            <h2 className="mb-4 font-serif text-[1.625rem] font-bold text-slate-900 min-[400px]:text-3xl sm:text-4xl md:text-5xl">
              {t('features.title')}
            </h2>
            <p className="text-base text-slate-500 sm:text-xl">{t('features.subtitle')}</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
            {[
              {
                icon: (
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                ),
                title: t('features.locationTracking.title'),
                description: t('features.locationTracking.description'),
                bg: 'bg-blue-50',
                text: 'text-blue-600',
                gradient: 'from-blue-500 to-cyan-500', // Keep for blob
              },
              {
                icon: (
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: t('features.transportRecognition.title'),
                description: t('features.transportRecognition.description'),
                bg: 'bg-emerald-50',
                text: 'text-emerald-600',
                gradient: 'from-green-500 to-emerald-500',
              },
              {
                icon: (
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                ),
                title: t('features.dataAnalysis.title'),
                description: t('features.dataAnalysis.description'),
                bg: 'bg-purple-50',
                text: 'text-purple-600',
                gradient: 'from-purple-500 to-pink-500',
              },
              {
                icon: (
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ),
                title: t('features.timeline.title'),
                description: t('features.timeline.description'),
                bg: 'bg-orange-50',
                text: 'text-orange-600',
                gradient: 'from-orange-500 to-red-500',
              },
              {
                icon: (
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                ),
                title: t('features.locationManagement.title'),
                description: t('features.locationManagement.description'),
                bg: 'bg-teal-50',
                text: 'text-teal-600',
                gradient: 'from-teal-500 to-cyan-500',
              },
              {
                icon: (
                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                    />
                  </svg>
                ),
                title: t('features.reports.title'),
                description: t('features.reports.description'),
                bg: 'bg-indigo-50',
                text: 'text-indigo-600',
                gradient: 'from-indigo-500 to-purple-500',
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="group relative rounded-2xl border border-white/60 bg-white/40 p-5 backdrop-blur-md transition-all duration-300 hover:bg-white/60 hover:shadow-xl sm:p-8 md:hover:scale-105"
              >
                <div className={`inline-flex rounded-xl p-3 ${feature.bg} ${feature.text} mb-4 shadow-sm`}>
                  {feature.icon}
                </div>
                <h3 className="mb-2 font-serif text-lg font-semibold text-slate-900 sm:mb-3 sm:text-xl">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-500 sm:text-base">{feature.description}</p>

                {/* Decorative gradient blob */}
                <div
                  className={`absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${feature.gradient} opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-10`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative z-10 px-4 py-16 sm:px-6 sm:py-24 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-10 text-center sm:mb-16">
            <h2 className="mb-4 font-serif text-[1.625rem] font-bold text-slate-900 min-[400px]:text-3xl sm:text-4xl md:text-5xl">
              {t('testimonials.title')}
            </h2>
            <p className="text-base text-slate-500 sm:text-xl">{t('testimonials.subtitle')}</p>
          </div>

          {/* Marquee Container */}
          <div className="relative overflow-hidden">
            {/* Gradient fade edges */}
            <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-16 bg-gradient-to-r from-slate-50 via-slate-50/80 to-transparent md:w-32" />
            <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-16 bg-gradient-to-l from-slate-50 via-slate-50/80 to-transparent md:w-32" />

            <div className="flex animate-marquee-mobile space-x-4 md:animate-marquee-desktop md:space-x-6">
              {/* First set of testimonials */}
              {(
                t('testimonials.reviews', { returnObjects: true }) as Array<{
                  text: string
                  author: string
                  role: string
                  avatar?: string
                }>
              ).map((review, index) => (
                <div
                  key={index}
                  className="group relative min-h-[240px] w-72 flex-shrink-0 rounded-2xl border border-white/60 bg-white/40 p-4 backdrop-blur-md transition-all duration-500 hover:scale-105 hover:border-white/80 hover:bg-white/60 hover:shadow-xl md:min-h-[280px] md:w-80 md:p-6"
                >
                  {/* Quote icon */}
                  <div className="mb-6 flex items-start justify-between">
                    <svg className="h-8 w-8 text-blue-100" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" />
                    </svg>
                    {/* Star rating */}
                    <div className="flex space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="h-4 w-4 fill-current text-yellow-400" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      ))}
                    </div>
                  </div>

                  {/* Review text */}
                  <div className="mb-6 flex-1">
                    <p className="line-clamp-4 text-base leading-relaxed text-slate-600">&ldquo;{review.text}&rdquo;</p>
                  </div>

                  {/* User info */}
                  <div className="mt-auto flex items-center">
                    <div className="relative">
                      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-100 to-purple-100 ring-2 ring-white">
                        {review.avatar ? (
                          <img src={review.avatar} alt={review.author} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-blue-600">{review.author.charAt(0)}</span>
                        )}
                      </div>
                      {/* Online indicator */}
                      <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-sm font-bold text-slate-900">{review.author}</div>
                      <div className="text-xs font-medium text-slate-400">{review.role}</div>
                    </div>
                    {/* Verified badge */}
                    <div className="ml-2" title="认证用户">
                      <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                        <path
                          fillRule="evenodd"
                          d="M12 2.5a2 2 0 0 1 1.732 1L15.18 6h2.82a2 2 0 0 1 1.414.586l.413.413A2 2 0 0 1 20 8.414V11.18l2.5 1.446a2 2 0 0 1 0 3.464L20 17.536v2.732a2 2 0 0 1-.586 1.414l-.413.413A2 2 0 0 1 17.586 22h-2.732L12.5 24.5a2 2 0 0 1-3.464 0L6.68 22H4.414a2 2 0 0 1-1.414-.586l-.413-.413A2 2 0 0 1 2 19.586V16.82L.5 15.374a2 2 0 0 1 0-3.464L2 10.464V7.732a2 2 0 0 1 .586-1.414l.413-.413A2 2 0 0 1 4.414 5.5H7.18L9.036 3.054A2 2 0 0 1 12 2.5zm4.891 6.758l-6.182 6.182-2.909-2.909a1 1 0 1 0-1.414 1.414l3.616 3.616a1 1 0 0 0 1.414 0l6.889-6.889a1 1 0 0 0-1.414-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}

              {/* Duplicate set for seamless loop */}
              {(
                t('testimonials.reviews', { returnObjects: true }) as Array<{
                  text: string
                  author: string
                  role: string
                  avatar?: string
                }>
              ).map((review, index) => (
                <div
                  key={`duplicate-${index}`}
                  className="group relative min-h-[240px] w-72 flex-shrink-0 rounded-2xl border border-white/60 bg-white/40 p-4 backdrop-blur-md transition-all duration-500 hover:scale-105 hover:border-white/80 hover:bg-white/60 hover:shadow-xl md:min-h-[280px] md:w-80 md:p-6"
                >
                  {/* Quote icon */}
                  <div className="mb-6 flex items-start justify-between">
                    <svg className="h-8 w-8 text-blue-100" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" />
                    </svg>
                    {/* Star rating */}
                    <div className="flex space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="h-4 w-4 fill-current text-yellow-400" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                      ))}
                    </div>
                  </div>

                  {/* Review text */}
                  <div className="mb-6 flex-1">
                    <p className="line-clamp-4 text-base leading-relaxed text-slate-600">&ldquo;{review.text}&rdquo;</p>
                  </div>

                  {/* User info */}
                  <div className="mt-auto flex items-center">
                    <div className="relative">
                      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-blue-100 to-purple-100 ring-2 ring-white">
                        {review.avatar ? (
                          <img src={review.avatar} alt={review.author} className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-blue-600">{review.author.charAt(0)}</span>
                        )}
                      </div>
                      {/* Online indicator */}
                      <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-sm font-bold text-slate-900">{review.author}</div>
                      <div className="text-xs font-medium text-slate-400">{review.role}</div>
                    </div>
                    {/* Verified badge */}
                    <div className="ml-2" title="认证用户">
                      <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
                        <path
                          fillRule="evenodd"
                          d="M12 2.5a2 2 0 0 1 1.732 1L15.18 6h2.82a2 2 0 0 1 1.414.586l.413.413A2 2 0 0 1 20 8.414V11.18l2.5 1.446a2 2 0 0 1 0 3.464L20 17.536v2.732a2 2 0 0 1-.586 1.414l-.413.413A2 2 0 0 1 17.586 22h-2.732L12.5 24.5a2 2 0 0 1-3.464 0L6.68 22H4.414a2 2 0 0 1-1.414-.586l-.413-.413A2 2 0 0 1 2 19.586V16.82L.5 15.374a2 2 0 0 1 0-3.464L2 10.464V7.732a2 2 0 0 1 .586-1.414l.413-.413A2 2 0 0 1 4.414 5.5H7.18L9.036 3.054A2 2 0 0 1 12 2.5zm4.891 6.758l-6.182 6.182-2.909-2.909a1 1 0 1 0-1.414 1.414l3.616 3.616a1 1 0 0 0 1.414 0l6.889-6.889a1 1 0 0 0-1.414-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 bg-white/50 px-4 py-16 sm:px-6 sm:py-24 md:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-2 gap-4 sm:gap-8 md:grid-cols-4 lg:gap-12">
            {[
              {
                number: '365',
                label: t('stats.daysRecorded'),
                icon: <CalendarDaysIcon className="h-10 w-10 text-blue-500" />,
              },
              {
                number: '4,351',
                label: t('stats.activitiesTracked'),
                icon: <BoltIcon className="h-10 w-10 text-orange-500" />,
              },
              {
                number: '738',
                label: t('stats.locationsDiscovered'),
                icon: <MapPinIcon className="h-10 w-10 text-red-500" />,
              },
              {
                number: '136',
                label: t('stats.citiesVisited'),
                icon: <BuildingOffice2Icon className="h-10 w-10 text-indigo-500" />,
              },
            ].map((stat, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-white/60 bg-white/40 p-4 text-center backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-6"
              >
                <div className="mb-4 flex justify-center transition-transform duration-300 group-hover:scale-110">
                  {stat.icon}
                </div>
                <div className="mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-2xl font-bold text-transparent sm:text-3xl md:text-4xl lg:text-5xl">
                  {stat.number}
                </div>
                <div className="text-sm font-medium text-slate-500 md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 px-4 py-16 sm:px-6 sm:py-24 md:px-12">
        <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl bg-slate-900 px-5 py-14 text-center shadow-2xl sm:rounded-[3rem] sm:px-6 sm:py-20 md:px-12">
          {/* Background effects */}
          <div className="absolute left-0 top-0 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-64 w-64 translate-x-1/2 translate-y-1/2 rounded-full bg-purple-500/20 blur-3xl" />

          <div className="relative z-10">
            <h2 className="mb-6 font-serif text-[1.625rem] font-bold text-white min-[400px]:text-3xl sm:mb-8 sm:text-4xl md:text-5xl lg:text-6xl">
              {t('cta.title')}
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-base text-slate-300 sm:mb-10 sm:text-xl">{t('cta.subtitle')}</p>
            <button
              onClick={() => {
                trackAppStoreClick()
                window.open('https://apps.apple.com/app/6481981341', '_blank')
              }}
              className="group relative inline-flex transform items-center justify-center rounded-full bg-white px-8 py-4 text-lg font-bold text-slate-900 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-white/20 sm:px-12 sm:py-5 sm:text-xl"
            >
              <svg className="relative z-10 mr-3 h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              <span className="relative z-10">{t('cta.button')}</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-200 bg-slate-50 px-4 py-10 sm:px-6 sm:py-16 md:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 sm:gap-12 md:grid-cols-4 lg:gap-16">
            <div className="md:col-span-2">
              <div className="mb-6 flex items-center space-x-3">
                <div className="overflow-hidden rounded-xl shadow-md">
                  <img src="/imgs/icon.png" alt="Retrace" className="h-10 w-10" />
                </div>
                <span className="text-2xl font-bold text-slate-900">{t('nav.appName')}</span>
              </div>
              <p className="mb-6 max-w-sm leading-relaxed text-slate-500">{t('footer.description')}</p>
            </div>
            <div>
              <h3 className="mb-6 text-lg font-bold text-slate-900">{t('footer.product.title')}</h3>
              <ul className="space-y-4 text-slate-500">
                <li className="cursor-pointer transition-colors hover:text-blue-600">{t('footer.product.features')}</li>
                <li className="cursor-pointer transition-colors hover:text-blue-600">{t('footer.product.guide')}</li>
                <li className="cursor-pointer transition-colors hover:text-blue-600">
                  {t('footer.product.changelog')}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="mb-6 text-lg font-bold text-slate-900">{t('footer.support.title')}</h3>
              <ul className="space-y-4 text-slate-500">
                <li className="cursor-pointer transition-colors hover:text-blue-600">{t('footer.support.help')}</li>
                <li>
                  <a
                    href="/deergo/privacy.html"
                    className="transition-colors duration-200 hover:text-blue-600"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('footer.support.privacy')}
                  </a>
                </li>
                <li>
                  <a
                    href="/deergo/eula.html"
                    className="transition-colors duration-200 hover:text-blue-600"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('footer.support.terms')}
                  </a>
                </li>
                <li className="cursor-pointer transition-colors hover:text-blue-600">{t('footer.support.contact')}</li>
              </ul>
            </div>
          </div>
          <div className="mt-16 border-t border-slate-100 pt-8 text-center text-slate-400">
            <div className="mb-4 space-x-6">
              <a
                href="/deergo/privacy.html"
                className="text-sm transition-colors duration-200 hover:text-slate-600"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('footer.support.privacy')}
              </a>
              <span className="text-slate-300">|</span>
              <a
                href="/deergo/eula.html"
                className="text-sm transition-colors duration-200 hover:text-slate-600"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('footer.support.terms')}
              </a>
            </div>
            <p className="text-sm">{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
